import React, { MouseEventHandler, ReactNode } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { contextMenuRules, styled, InteractiveElement } from "@iventis/styles";
import { css, Theme } from "@emotion/react";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { StyledPopover } from "./styled-popover";
import { SimpleTooltip } from "./tooltips";
import { DisabledOverlay } from "./disabled-overlay";

type ActionDropdownTheme = "primary" | "secondary";

export interface DropdownActions {
    displayName: string;
    onClick: () => void;
    testId: string;
    primaryIcon: ReactNode | string[];
    secondaryIcon?: ReactNode;
    divider?: boolean;
    disabled?: boolean;
}

interface ActionDropdownButtonProps {
    clickHandler: MouseEventHandler<HTMLButtonElement>;
    menuItems: DropdownActions[];
    variant: ActionDropdownTheme;
    actionIcon: ReactNode;
    disable: boolean;
    primaryButtonTestId?: string;
    secondaryButtonTestId?: string;
    tooltipPrimary?: string | ReactNode;
    tooltipSecondary?: string | ReactNode;
    isSelected?: boolean;
    disableSecondary?: boolean;
}

export const ActionDropdownButton: React.FC<ActionDropdownButtonProps> = ({
    clickHandler,
    variant,
    actionIcon,
    menuItems,
    disable,
    primaryButtonTestId,
    secondaryButtonTestId,
    tooltipPrimary,
    tooltipSecondary,
    isSelected,
    disableSecondary,
}) => {
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? "simple-popover" : undefined;

    return (
        <StyledContainer $color={variant} $disabled={disable}>
            <SimpleTooltip text={tooltipPrimary} placement="bottom">
                <span>
                    <LeftButton $color={variant} $disabled={disable} $selected={isSelected} onClick={clickHandler} disabled={disable} data-testid={primaryButtonTestId}>
                        <StyledIconContainer $color={variant} $disabled={disable}>
                            {actionIcon}
                        </StyledIconContainer>
                    </LeftButton>
                </span>
            </SimpleTooltip>
            <SimpleTooltip text={tooltipSecondary} placement="bottom">
                <span>
                    <RightButton
                        $color={variant}
                        $disabled={disable || disableSecondary}
                        $selected={isSelected}
                        onClick={handleClick}
                        disabled={disable || disableSecondary}
                        data-testid={secondaryButtonTestId}
                    >
                        <StyledIconContainer $color={variant} $disabled={disable || disableSecondary}>
                            <FontAwesomeIcon size="sm" icon={{ prefix: "fas", iconName: "chevron-down" }} />
                        </StyledIconContainer>
                    </RightButton>
                </span>
            </SimpleTooltip>

            <Dropdown
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                space={44}
            >
                {menuItems.map(
                    (item) => !!Object.keys(item).length && <DropdownMenuItem key={item.displayName} menuItem={item} handleClose={handleClose} divider={item.divider} />
                )}
            </Dropdown>
        </StyledContainer>
    );
};

export const DropdownMenuItem = ({ menuItem, handleClose, divider }: { menuItem: DropdownActions; handleClose: () => void; divider: boolean }) => (
    <StyledMenuItemContainer $divider={divider}>
        <span style={{ position: "relative" }}>
            {menuItem.disabled && <DisabledOverlay />}
            <StyledContextMenuInteractiveElement
                data-testid={menuItem.testId}
                className="button"
                onClick={() => {
                    handleClose();
                    menuItem.onClick();
                }}
            >
                <StyledNameContainer>
                    {Array.isArray(menuItem.primaryIcon) ? (
                        <StyledActionDropdownIconContainer>
                            <FontAwesomeIcon icon={menuItem.primaryIcon as IconProp} />
                        </StyledActionDropdownIconContainer>
                    ) : (
                        menuItem.primaryIcon
                    )}
                    <span>{menuItem.displayName}</span>
                </StyledNameContainer>

                {menuItem.secondaryIcon || null}
            </StyledContextMenuInteractiveElement>
        </span>
    </StyledMenuItemContainer>
);
const StyledContainer = styled.div<{ $color: ActionDropdownTheme; $disabled: boolean }>`
    border-radius: 4px;
    background-color: ${({ theme, $color }: { theme: Theme; $color: ActionDropdownTheme }) =>
        $color === "primary" ? theme.typographyColors.primary : theme.typographyColors.blank};
    ${({ $disabled, theme, $color }) =>
        $disabled &&
        `background-color: 
            ${$color === "secondary" ? theme.typographyColors.blank : "rgba(0, 0, 0, 0.12)"};
            color: ${$color === "secondary" ? theme.shades.darkBorder : theme.typographyColors.blank};`};
    display: flex;
    align-items: center;
    width: 60px;
    padding: 0.3125em;
`;

const StyledIconContainer = styled.span<{ $disabled: boolean; $color: ActionDropdownTheme }>`
    color: ${({ theme, $disabled, $color }: { theme: Theme; $disabled: boolean; $color: ActionDropdownTheme }) =>
        $disabled ? theme.shades.darkBorder : $color === "secondary" ? theme.typographyColors.core : theme.typographyColors.blank};
`;

const ButtonStyles = ({ theme, $disabled }: { theme: Theme; $disabled: boolean; $color: ActionDropdownTheme }) => css`
    color: ${theme.typographyColors.core};
    transition: 150ms ease-in-out;
    min-height: 34px;
    padding: 0;
    border-radius: 4px;
    cursor: ${$disabled ? "not-allowed" : "pointer"};
`;

const LeftButton = styled(InteractiveElement)<{ $disabled: boolean; $color: ActionDropdownTheme; $selected: boolean }>`
    width: 40px;
    background-color: ${({ theme, $color, $disabled, $selected }: { theme: Theme; $color: ActionDropdownTheme; $disabled: boolean; $selected: boolean }) =>
        $color === "secondary" && !$disabled && $selected ? theme.primaryColors.subdued70 : "transparent"};
    svg {
        color: ${({ theme, $color, $disabled, $selected }: { theme: Theme; $color: ActionDropdownTheme; $disabled: boolean; $selected: boolean }) =>
            $color === "primary" && !$disabled && !$selected
                ? theme.secondaryColors.light50
                : $color === "secondary" && !$disabled && !$selected
                ? theme.typographyColors.core
                : !$disabled
                ? theme.typographyColors.blank
                : "inherit"};
    }
    ${({ theme, $color, $disabled, $selected }: { theme: Theme; $color: ActionDropdownTheme; $disabled: boolean; $selected: boolean }) =>
        !$selected &&
        css`
            :hover {
                background-color: ${$color === "primary" && !$disabled
                    ? theme.secondaryColors.light50
                    : $color === "secondary" && !$disabled
                    ? theme.tertiaryColors.primaryBackground
                    : "transparent"};
                svg {
                    color: ${$color === "primary" && !$disabled ? theme.typographyColors.primary : $color === "secondary" && !$disabled ? theme.typographyColors.core : "inherit"};
                }
            }
        `}

    :active {
        transition: 150ms ease-in-out;
        background-color: ${({ theme, $color, $disabled }: { theme: Theme; $color: ActionDropdownTheme; $disabled: boolean }) =>
            $color === "primary" && !$disabled ? theme.secondaryColors.light50 : $color === "secondary" && !$disabled ? theme.primaryColors.subdued70 : "transparent"};
        svg {
            color: ${({ theme, $color, $selected }: { theme: Theme; $color: ActionDropdownTheme; $selected: boolean }) =>
                $selected ? theme.typographyColors.blank : $color === "primary" ? theme.typographyColors.primary : theme.typographyColors.core};
        }
    }

    :focus {
        background-color: ${({ theme, $color }: { theme: Theme; $color: ActionDropdownTheme }) =>
            $color === "primary" ? theme.typographyColors.primary : theme.primaryColors.subdued70};
        svg {
            color: ${({ theme, $color, $selected }: { theme: Theme; $color: ActionDropdownTheme; $selected: boolean }) =>
                $selected ? theme.typographyColors.blank : $color === "primary" ? theme.secondaryColors.light50 : theme.typographyColors.blank};
        }
    }
    ${ButtonStyles};
`;
const RightButton = styled(InteractiveElement)<{ $disabled: boolean; $color: ActionDropdownTheme; $selected: boolean }>`
    width: 22px;
    :hover {
        background-color: ${({ theme, $color, $disabled }: { theme: Theme; $color: ActionDropdownTheme; $disabled: boolean }) =>
            $color === "primary" && !$disabled ? theme.secondaryColors.light50 : $color === "secondary" && !$disabled ? theme.tertiaryColors.primaryBackground : "transparent"};
        svg {
            color: ${({ theme, $color, $disabled }: { theme: Theme; $color: ActionDropdownTheme; $disabled: boolean }) =>
                $color === "primary" && !$disabled ? theme.typographyColors.primary : $color === "secondary" && !$disabled ? theme.typographyColors.core : "inherit"};
        }
    }

    :active {
        background-color: ${({ theme, $color, $disabled }: { theme: Theme; $color: ActionDropdownTheme; $disabled: boolean }) =>
            $color === "primary" && !$disabled ? theme.secondaryColors.light50 : $color === "secondary" && !$disabled ? theme.typographyColors.blank : "transparent"};
        svg {
            color: ${({ theme, $color }: { theme: Theme; $color: ActionDropdownTheme }) => ($color === "primary" ? theme.typographyColors.primary : theme.typographyColors.core)};
        }
    }

    :focus {
        background-color: ${({ theme, $color }: { theme: Theme; $color: ActionDropdownTheme }) =>
            $color === "primary" ? theme.typographyColors.primary : theme.typographyColors.blank};
        svg {
            color: ${({ theme, $color }: { theme: Theme; $color: ActionDropdownTheme }) => ($color === "primary" ? theme.secondaryColors.light50 : theme.typographyColors.core)};
        }
    }
    ${ButtonStyles};
`;

const Dropdown = styled(StyledPopover)`
    // Disable mouse events on the MuiPopper but enable them on the paper, ensure click events can still occur outside the context menu
    .enable-mouse-events {
        pointer-events: auto;
    }

    .disable-mouse-events {
        pointer-events: none;
    }

    .MuiPopover-paper {
        border-radius: ${contextMenuRules.borderRadius};
        padding: ${contextMenuRules.padding};
        grid-auto-rows: unset;
    }

    top: 1rem;
    left: -2rem;
`;

const StyledMenuItemContainer = styled.span<{ $divider: boolean }>`
    height: 44px;
    border-bottom: ${({ theme, $divider }: { theme: Theme; $divider: boolean }) => $divider && `1px solid ${theme.tertiaryColors.headerBackground}`};
`;

const StyledActionDropdownIconContainer = styled.span`
    width: 18px;
    height: 14px;
`;

const StyledNameContainer = styled.span`
    display: flex;
    gap: 0.625em;
`;

const StyledContextMenuInteractiveElement = styled(InteractiveElement)`
    border-radius: ${contextMenuRules.borderRadius};
    display: flex;
    justify-content: space-between;
    gap: 0.5rem;
    height: 44px;
    min-width: 148px !important;
`;
